import React from 'react';
import uuidv4 from 'uuid/v4';
import { CreateFlagArgs, FlagArgs, AppearanceTypes } from '@atlaskit/flag';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import InfoIcon from '@atlaskit/icon/glyph/info';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { N500, Green500, Red500, Yellow500 } from '@trello/colors';
import { flagsState } from './flagsState';

export const dismissFlag = (id: string | number) => {
  flagsState.setValue((current: FlagArgs[]) =>
    current.filter((flag) => flag.id !== id),
  );
};

interface ShowFlagArgs extends Omit<CreateFlagArgs, 'icon'> {
  icon?: React.ReactNode;
}

// Most of the time, rendering an icon when dispatching a flag is too bulky;
// this maps the existing appearance icon to a common icon. Custom icons are
// still supported where desired.
const mapAppearanceToIcon = (appearance?: AppearanceTypes): React.ReactNode => {
  switch (appearance) {
    case 'error':
      return <ErrorIcon label="Error" primaryColor={Red500} />;
    case 'success':
      return <CheckCircleIcon label="Success" primaryColor={Green500} />;
    case 'warning':
      return <WarningIcon label="Warning" primaryColor={Yellow500} />;
    case 'info':
    case 'normal':
    default:
      return <InfoIcon label="Info" primaryColor={N500} />;
  }
};

export const showFlag = (value: ShowFlagArgs) => {
  const flag: FlagArgs = {
    ...value,
    id: value.id || uuidv4(),
    // Hardcode all appearances to be `normal`; AK Flags are super opinionated
    // about how different appearances look, and don't currently support themes.
    appearance: 'normal',
    icon: value.icon ?? mapAppearanceToIcon(value.appearance),
  };
  flagsState.setValue((current: FlagArgs[]) => {
    const index = current.findIndex(({ id }) => id === flag.id);
    // If flag is not found, add it
    if (index === -1) {
      return [flag, ...current];
    }
    // If flag already exists, replace it
    const shallow: FlagArgs[] = [...current];
    shallow[index] = flag;
    return shallow;
  });
  return () => dismissFlag(flag.id);
};
